import React, { useState } from 'react'
import { X, Download, Heart, Trash2, Share2, Eye, EyeOff, Calendar, Filter, Copy, Check, Image as ImageIcon } from 'lucide-react'
import CommentSection from '../Comments/CommentSection'
import { updateImage } from '../../lib/supabase'

interface ImageItem {
  id: string
  title: string
  description?: string
  prompt_text: string
  image_url: string
  reference_images?: string[] // Array of reference image URLs
  filters_used: Record<string, any>
  is_public: boolean
  is_nsfw: boolean
  likes_count: number
  views_count: number
  created_at: string
  updated_at: string
}

interface ImageViewerProps {
  image: ImageItem
  onClose: () => void
  onDelete: (image: ImageItem) => void
  onToggleFavorite: (imageId: string) => void
  isFavorited: boolean
  newGenerations?: any[]
  onImageUpdate?: (updatedImage: ImageItem) => void
}

const ImageViewer: React.FC<ImageViewerProps> = ({
  image,
  onClose,
  onDelete,
  onToggleFavorite,
  isFavorited,
  onImageUpdate
}) => {
  const [copiedPrompt, setCopiedPrompt] = useState(false)
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false)
  const [currentImage, setCurrentImage] = useState(image)

  const handleToggleVisibility = async () => {
    try {
      setIsUpdatingVisibility(true)
      
      const updatedImage = await updateImage(currentImage.id, {
        is_public: !currentImage.is_public
      })
      
      if (updatedImage) {
        setCurrentImage(updatedImage)
        onImageUpdate?.(updatedImage)
      }
    } catch (error) {
      console.error('Error updating image visibility:', error)
      alert('Failed to update image visibility. Please try again.')
    } finally {
      setIsUpdatingVisibility(false)
    }
  }

  const handleDownload = async () => {
    try {
      console.log('📥 Starting image download:', { url: currentImage.image_url, title: currentImage.title })
      
      // Clean the title for filename
      const cleanTitle = currentImage.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()
      const filename = `toefetti_${cleanTitle}.png`
      
      // Try direct download first
      const link = document.createElement('a')
      link.href = currentImage.image_url
      link.download = filename
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      console.log('✅ Download initiated for:', filename)
    } catch (error) {
      console.error('❌ Download failed:', error)
      
      // Fallback: open in new tab
      try {
        window.open(currentImage.image_url, '_blank', 'noopener,noreferrer')
        alert('Download failed. Image opened in new tab - right-click and select "Save image as..." to download.')
      } catch (fallbackError) {
        alert('Download failed. Please right-click the image and select "Save image as..." to download manually.')
      }
    }
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Toefetti: ${currentImage.title}`,
          text: currentImage.prompt_text,
          url: window.location.href
        })
      } catch (error) {
        console.log('Error sharing:', error)
      }
    } else {
      // Fallback: copy image URL to clipboard
      navigator.clipboard.writeText(currentImage.image_url)
      alert('Image URL copied to clipboard!')
    }
  }

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(currentImage.prompt_text)
      setCopiedPrompt(true)
      setTimeout(() => setCopiedPrompt(false), 2000)
    } catch (error) {
      console.error('Failed to copy prompt:', error)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getActiveFilters = () => {
    return Object.entries(currentImage.filters_used)
      .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
      .map(([key, value]) => ({ key, value }))
  }

  const activeFilters = getActiveFilters()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
          <h2 className="text-xl font-bold text-gray-900 truncate">{currentImage.title}</h2>
          <div className="flex items-center space-x-2">
            {/* Action buttons */}
            <button
              onClick={() => onToggleFavorite(currentImage.id)}
              className={`p-2 rounded-full transition-colors ${
                isFavorited 
                  ? 'bg-red-50 text-red-500' 
                  : 'bg-gray-50 text-gray-400 hover:text-red-500'
              }`}
            >
              <Heart className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
            </button>
            <button
              onClick={handleDownload}
              className="p-2 bg-gray-50 text-gray-400 hover:text-blue-500 rounded-full transition-colors"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={handleShare}
              className="p-2 bg-gray-50 text-gray-400 hover:text-green-500 rounded-full transition-colors"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <button
              onClick={() => onDelete(currentImage)}
              className="p-2 bg-gray-50 text-gray-400 hover:text-red-500 rounded-full transition-colors"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <button 
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row">
          {/* Image Section */}
          <div className="lg:w-2/3 p-6">
            <div className="relative bg-gray-100 rounded-xl overflow-hidden">
              <img
                src={currentImage.image_url}
                alt={currentImage.title}
                className="w-full h-auto object-contain max-h-[60vh]"
              />
              
              {/* Image overlay info */}
              <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                <div className="flex space-x-2">
                  {currentImage.is_public && (
                    <div className="flex items-center space-x-1 px-2 py-1 bg-green-500 text-white rounded-full text-sm">
                      <Eye className="w-3 h-3" />
                      <span>Public</span>
                    </div>
                  )}
                  {activeFilters.length > 0 && (
                    <div className="px-2 py-1 bg-black bg-opacity-70 text-white rounded-full text-sm">
                      {activeFilters.length} filters applied
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-3 text-white text-sm">
                  <span className="bg-black bg-opacity-70 px-2 py-1 rounded-full">
                    {currentImage.views_count} views
                  </span>
                  <span className="bg-black bg-opacity-70 px-2 py-1 rounded-full">
                    {currentImage.likes_count} likes
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="lg:w-1/3 p-6 border-l border-gray-200 space-y-6">
            {/* Creation Date */}
            <div className="flex items-center space-x-2 text-gray-500">
              <Calendar className="w-4 h-4" />
              <span className="text-sm">Created {formatDate(currentImage.created_at)}</span>
            </div>

            {/* Description */}
            {currentImage.description && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-600 text-sm">{currentImage.description}</p>
              </div>
            )}

            {/* Prompt */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">Prompt</h3>
                <button
                  onClick={copyPrompt}
                  className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {copiedPrompt ? (
                    <>
                      <Check className="w-3 h-3" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700">{currentImage.prompt_text}</p>
              </div>
            </div>

            {/* Reference Images */}
            {currentImage.reference_images && currentImage.reference_images.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <ImageIcon className="w-4 h-4 text-gray-500" />
                  <h3 className="font-semibold text-gray-900">Reference Images</h3>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {currentImage.reference_images.length} image{currentImage.reference_images.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {currentImage.reference_images.map((imageUrl, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={imageUrl}
                        alt={`Reference ${index + 1}`}
                        className="w-full h-20 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => window.open(imageUrl, '_blank')}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                        <Eye className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Click on any reference image to view full size
                </p>
              </div>
            )}

            {/* Applied Filters */}
            {activeFilters.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <h3 className="font-semibold text-gray-900">Applied Filters</h3>
                </div>
                <div className="space-y-2">
                  {activeFilters.map(({ key, value }) => (
                    <div key={key} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <span className="text-sm text-gray-600 bg-white px-2 py-1 rounded">
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stats */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Statistics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-lg font-bold text-blue-600">{currentImage.views_count}</div>
                  <div className="text-xs text-blue-500">Views</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-lg font-bold text-red-600">{currentImage.likes_count}</div>
                  <div className="text-xs text-red-500">Likes</div>
                </div>
              </div>
            </div>

            {/* Visibility Toggle */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Visibility</h3>
              <div className={`p-3 rounded-lg border ${
                currentImage.is_public ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {currentImage.is_public ? (
                      <>
                        <Eye className="w-4 h-4 text-green-600" />
                        <div>
                          <span className="text-sm text-green-700 font-medium">Public</span>
                          <p className="text-xs text-green-600">Visible to everyone in Discover</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <EyeOff className="w-4 h-4 text-gray-600" />
                        <div>
                          <span className="text-sm text-gray-700 font-medium">Private</span>
                          <p className="text-xs text-gray-600">Only visible to you</p>
                        </div>
                      </>
                    )}
                  </div>
                  
                  <button
                    onClick={handleToggleVisibility}
                    disabled={isUpdatingVisibility}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      currentImage.is_public
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        : 'bg-green-500 text-white hover:bg-green-600'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isUpdatingVisibility ? 'Updating...' : (currentImage.is_public ? 'Make Private' : 'Make Public')}
                  </button>
                </div>
              </div>
            </div>

            {/* Comments Section - Only show for public images */}
            {currentImage.is_public && (
              <div className="pt-4 border-t border-gray-200">
                <CommentSection imageId={currentImage.id} isPublic={currentImage.is_public} />
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleDownload}
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download Image</span>
              </button>
              
              <button
                onClick={handleShare}
                className="w-full btn-secondary flex items-center justify-center space-x-2"
              >
                <Share2 className="w-4 h-4" />
                <span>Share Image</span>
              </button>
              
              <button
                onClick={() => onToggleFavorite(currentImage.id)}
                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-xl font-medium transition-colors ${
                  isFavorited
                    ? 'bg-red-50 text-red-600 hover:bg-red-100'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
                <span>{isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}</span>
              </button>
              
              <button
                onClick={() => onDelete(currentImage)}
                className="w-full bg-red-50 text-red-600 hover:bg-red-100 px-4 py-2 rounded-xl font-medium transition-colors flex items-center justify-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Image</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageViewer 